import { useCallback, useEffect, useRef, useState } from 'react';
import StripeLanguageNote from '../i18n/StripeLanguageNote';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { translate, useT } from '../i18n/i18n';
import { useLearner } from '../learners/LearnerContext';
import { NEED_LABEL, describe, hours, money, useAssistAccess } from './useAssistAccess';

type Expert = {
  id: string; name: string; headline: string; bio: string; specialisms: string[]; qualifications: string;
  registration: string; dbsChecked: boolean; dbsDate: string | null; hourPence: number;
  rating: { average: number | null; count: number; recent: { id: string; rating: number; comment: string | null; createdAt: string }[] };
};
export type Work = {
  id: string; status: string; expertName: string; familyName: string | null; familyEmail: string | null;
  learners: { id: string; name: string }[]; needs: string[]; request: string; hourPence: number;
  minutesBought: number; minutesUsed: number; createdAt: string; endedAt: string | null;
  time: { minutes: number; note: string; at: string }[]; changes: { action: string; learner: string | null; at: string }[];
  refundStatus: string; refundedMinutes: number; refundedPence: number;
  quoteMinutes: number | null; quoteNote: string | null; quotePence: number | null; declineNote: string | null; reviewed: boolean;
};

const STATUS: Record<string, string> = {
  REQUESTED: 'Waiting for an estimate', QUOTED: 'Estimate ready', ACTIVE: 'Working',
  ENDED: 'Ended', DECLINED: 'Not taken on', CANCELLED: 'Cancelled',
};
const TERMS_VERSION = '2026-10-21';
const RATINGS = ['1 – Poor', '2 – Not so good', '3 – Okay', '4 – Good', '5 – Very good'];
const back = () => `${window.location.origin}/assist/experts`;

function Review({ w }: { w: Work }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    setBusy(true);
    setError(null);
    try {
      await api(`/assist/engagements/${w.id}/review`, { method: 'POST', token: true, body: { rating, comment } });
      setSent(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be done.'));
    } finally {
      setBusy(false);
    }
  }

  if (sent) return <p role="status" style={{ margin: 0 }}>{t('Thank you for your review.')}</p>;
  if (!open) return <div className="toolbar"><button type="button" className="btn secondary" onClick={() => setOpen(true)}>{t('Review {name}', { name: w.expertName })}</button></div>;

  return (
    <div className="assist-form">
      <fieldset className="ai-data">
        <legend className="field-label">{t('How was the work?')}</legend>
        {RATINGS.map((label, i) => (
          <label key={label} className="ai-data-option">
            <input type="radio" name={`rating-${w.id}`} checked={rating === i + 1} onChange={() => setRating(i + 1)} />
            <span>{t(label)}</span>
          </label>
        ))}
      </fieldset>
      <div className="field">
        <label htmlFor={`review-${w.id}`}>{t('Anything other families should know (optional)')}</label>
        <textarea id={`review-${w.id}`} rows={3} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} />
        <p className="field-hint">{t('Other families see your rating and comment, but not your name.')}</p>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      <div className="form-actions">
        <button type="button" className="btn" disabled={busy || rating === null} onClick={() => void send()}>{t('Send review')}</button>
        <button type="button" className="btn secondary" disabled={busy} onClick={() => setOpen(false)}>{t('Cancel')}</button>
      </div>
    </div>
  );
}

function Concern({ w, onSent }: { w: Work; onSent: () => void }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [safeguarding, setSafeguarding] = useState(false);
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    setBusy(true);
    setError(null);
    try {
      await api(`/assist/engagements/${w.id}/concern`, { method: 'POST', token: true, body: { safeguarding, details } });
      setSent(true);
      onSent();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be done.'));
    } finally {
      setBusy(false);
    }
  }

  if (sent) return <p role="status" style={{ margin: 0 }}>{t('Thank you. We will look into this and reply by email.')}</p>;
  if (!open) return <div className="toolbar"><button type="button" className="btn quiet-danger" onClick={() => setOpen(true)}>{t('Report a concern')}</button></div>;

  return (
    <div className="assist-form">
      <p style={{ margin: 0 }}><strong>{t('If a child is in danger now, call 999.')}</strong></p>
      <div className="field">
        <label htmlFor={`concern-${w.id}`}>{t('What happened')}</label>
        <textarea id={`concern-${w.id}`} rows={4} maxLength={4000} value={details} onChange={(e) => setDetails(e.target.value)} />      </div>
      <label className="studio-photo-check">
        <input type="checkbox" checked={safeguarding} onChange={(e) => setSafeguarding(e.target.checked)} />
        <span>{t("This is about a child's safety (safeguarding)")}</span>
      </label>
      {safeguarding && <p className="field-hint" style={{ margin: 0 }}>{t('We will pause {name} straight away and end their access to your learners while we look into it.', { name: w.expertName })}</p>}
      {error && <p className="error" role="alert">{error}</p>}
      <div className="form-actions">
        <button type="button" className="btn" disabled={busy || !details.trim()} onClick={() => void send()}>{t('Send')}</button>
        <button type="button" className="btn secondary" disabled={busy} onClick={() => setOpen(false)}>{t('Cancel')}</button>
      </div>
    </div>
  );
}

function Booking({ expert, onDone, onSent }: { expert: Expert; onDone: () => void; onSent: () => void }) {
  const t = useT();
  const { learners, learnerId } = useLearner();
  const [chosen, setChosen] = useState<string[]>(learnerId ? [learnerId] : []);
  const [needs, setNeeds] = useState<string[]>([]);
  const [request, setRequest] = useState('');
  const [consent, setConsent] = useState(false);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toggle = (list: string[], set: (v: string[]) => void, v: string) => set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const names = learners.filter((l) => chosen.includes(l.id)).map((l) => l.name).join(` ${t('and')} `) || t('the learners you choose');

  async function send() {
    setBusy(true);
    setError(null);
    try {
      await api('/assist/engagements', { method: 'POST', token: true, body: { expertId: expert.id, learnerIds: chosen, needs, request, consent, termsVersion: TERMS_VERSION } });
      onSent();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be done.'));
      setBusy(false);
    }
  }

  return (
    <div className="assist-booking">
      <fieldset className="ai-data">
        <legend className="field-label">{t('Which learners')}</legend>
        {learners.map((l) => (
          <label key={l.id} className="ai-data-option">
            <input type="checkbox" checked={chosen.includes(l.id)} onChange={() => toggle(chosen, setChosen, l.id)} />
            <span>{l.name}</span>
          </label>
        ))}
      </fieldset>
      <fieldset className="ai-data">
        <legend className="field-label">{t('What you need')}</legend>
        {Object.entries(NEED_LABEL).map(([id, label]) => (
          <label key={id} className="ai-data-option">
            <input type="checkbox" checked={needs.includes(id)} onChange={() => toggle(needs, setNeeds, id)} />
            <span>{t(label)}</span>
          </label>
        ))}
      </fieldset>
      <div className="field">
        <label htmlFor={`req-${expert.id}`}>{t('Tell {name} about it', { name: expert.name })}</label>
        <textarea id={`req-${expert.id}`} rows={4} maxLength={2000} value={request} onChange={(e) => setRequest(e.target.value)}
          placeholder={t('What you would like help with, what is going well, and what is hard.')} />
        <p className="field-hint">{t('Sending a request is free. {name} replies with how long it will take and what it will cost. You pay only if you accept, and any time not used is refunded to your card.', { name: expert.name })}</p>
      </div>
      <label className="studio-photo-check">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
        <span>{t("I agree that {name} can see my request, my name and email address, and the names of {names}. If I accept their estimate, they can also see {names}'s programs, progress and weekly plan, and make and change programs and the weekly plan, until I end access. I see every change they make.", { name: expert.name, names })}</span>
      </label>
      <label className="studio-photo-check">
        <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
        <span>{t('I have read and accept the Assist terms')}</span>
      </label>
      <p className="field-hint" style={{ margin: 0 }}><a href="/assist/terms" target="_blank" rel="noopener noreferrer">{t('Read the Assist terms')}</a></p>
      {error && <p className="error" role="alert">{error}</p>}
      <div className="form-actions">
        <button type="button" className="btn" disabled={busy || !consent || !terms || chosen.length === 0} onClick={() => void send()}>{t('Send request')}</button>
        <button type="button" className="btn secondary" disabled={busy} onClick={onDone}>{t('Cancel')}</button>
      </div>
    </div>
  );
}

function WorkCard({ w, onChange }: { w: Work; onChange: () => void }) {
  const t = useT();
  const [more, setMore] = useState(2);
  const [startNow, setStartNow] = useState(false);
  const [understands, setUnderstands] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const left = w.minutesBought - w.minutesUsed;

  async function run(path: string, body?: unknown, redirect = false) {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ url?: string }>(path, { method: 'POST', token: true, body });
      if (redirect && r.url) { window.location.href = r.url; return; }
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be done.'));
    } finally {
      setBusy(false);
    }
  }

  function end() {
    const question = left > 0
      ? t("End {name}'s access? They will no longer be able to see or change anything. The {time} not used is refunded to your card.", { name: w.expertName, time: hours(left) })
      : t("End {name}'s access? They will no longer be able to see or change anything.", { name: w.expertName });
    if (!window.confirm(question)) return;
    void run(`/assist/engagements/${w.id}/end`);
  }

  const learnerNames = w.learners.map((l) => l.name).join(` ${t('and')} `);

  return (
    <article className="assist-work">
      <div className="studio-type-head">
        <h3 style={{ margin: 0 }}>{w.expertName}</h3>
        <span className="pill">{STATUS[w.status] ? t(STATUS[w.status]) : w.status}</span>
      </div>
      <p className="muted" style={{ margin: 0 }}>{t('For {learners}', { learners: learnerNames })}{w.needs.length ? ` · ${w.needs.map((n) => (NEED_LABEL[n] ? t(NEED_LABEL[n]) : n)).join(', ')}` : ''}</p>

      {w.status === 'REQUESTED' && (
        <>
          <p style={{ margin: 0 }}>{t('{name} has your request and will reply with an estimate.', { name: w.expertName })}</p>
          <div className="toolbar"><button type="button" className="btn secondary" disabled={busy} onClick={() => void run(`/assist/engagements/${w.id}/withdraw`)}>{t('Cancel request')}</button></div>
        </>
      )}
      {w.status === 'QUOTED' && w.quoteMinutes != null && w.quotePence != null && (
        <>
          <p style={{ margin: 0 }}><strong>{t('{name} estimates {time}, which costs {price}.', { name: w.expertName, time: describe(w.quoteMinutes), price: money(w.quotePence) })}</strong></p>
          {w.quoteNote && <p className="ai-text">{w.quoteNote}</p>}
          <p className="field-hint">{t('If it takes less time, the rest is refunded to your card. If it needs more, {name} has to ask you first.', { name: w.expertName })}</p>
          <label className="studio-photo-check">
            <input type="checkbox" checked={startNow} onChange={(e) => setStartNow(e.target.checked)} />
            <span>{t('I ask {name} to start straight away, within the 14-day cancellation period.', { name: w.expertName })}</span>
          </label>
          <label className="studio-photo-check">
            <input type="checkbox" checked={understands} onChange={(e) => setUnderstands(e.target.checked)} />
            <span>{t('I understand I can cancel within 14 days, I pay only for time already used, and I lose the right to cancel once the work is fully done. Unused time is always refunded.')}</span>
          </label>
          <StripeLanguageNote />
          <div className="toolbar">
            <button type="button" className="btn" disabled={busy || !startNow || !understands} onClick={() => void run(`/assist/engagements/${w.id}/accept`, { returnUrl: back(), startNow: true, understandsCancellation: true }, true)}>{t('Accept and pay {price}', { price: money(w.quotePence) })}</button>
            <button type="button" className="btn secondary" disabled={busy} onClick={() => void run(`/assist/engagements/${w.id}/withdraw`)}>{t('Decline')}</button>
          </div>
        </>
      )}
      {w.status === 'DECLINED' && <p style={{ margin: 0 }}>{t('{name} could not take this on.', { name: w.expertName })}{w.declineNote ? ` ${w.declineNote}` : ''}</p>}

      {(w.status === 'ACTIVE' || w.status === 'ENDED') && <p style={{ margin: 0 }}>{w.status === 'ACTIVE'
        ? t('{used} used of {bought} bought, {left} left.', { used: hours(w.minutesUsed), bought: hours(w.minutesBought), left: hours(left) })
        : t('{used} used of {bought} bought.', { used: hours(w.minutesUsed), bought: hours(w.minutesBought) })}</p>}
      {w.status === 'ENDED' && w.refundedPence > 0 && <p style={{ margin: 0 }}>{t('Refunded {price} to your card for {time} not used.', { price: money(w.refundedPence), time: hours(w.refundedMinutes) })}</p>}
      {w.status === 'ENDED' && (w.refundStatus === 'PENDING' || w.refundStatus === 'FAILED') && <p style={{ margin: 0 }}>{t('The refund for time not used is being processed.')}</p>}
      {w.changes.length > 0 && (
        <details>
          <summary className="studio-data-summary">{t('What {name} changed ({count})', { name: w.expertName, count: w.changes.length })}</summary>
          <ul className="assist-log">{w.changes.map((c, i) => <li key={i}>{new Date(c.at).toLocaleString()}: {c.action}{c.learner ? ` ${t('for {learner}', { learner: c.learner })}` : ''}</li>)}</ul>
        </details>
      )}
      {w.time.length > 0 && (
        <details>
          <summary className="studio-data-summary">{t('Time logged ({count})', { count: w.time.length })}</summary>
          <ul className="assist-log">{w.time.map((entry, i) => <li key={i}>{new Date(entry.at).toLocaleDateString()}: {t('{n} min', { n: entry.minutes })}{entry.note ? ` · ${entry.note}` : ''}</li>)}</ul>
        </details>
      )}
      {error && <p className="error" role="alert">{error}</p>}
      {w.status === 'ACTIVE' && (
        <div className="toolbar">
          <select aria-label={t('Hours to add')} value={more} onChange={(e) => setMore(Number(e.target.value))}>
            {[1, 2, 3, 4, 5, 6, 8, 10].map((h) => <option key={h} value={h}>{h === 1 ? t('{h} hour, {price}', { h, price: money(w.hourPence * h) }) : t('{h} hours, {price}', { h, price: money(w.hourPence * h) })}</option>)}
          </select>
          <button type="button" className="btn secondary" disabled={busy} onClick={() => void run(`/assist/engagements/${w.id}/hours`, { hours: more, returnUrl: back() }, true)}>{t('Buy more hours')}</button>
          <button type="button" className="btn quiet-danger" disabled={busy} onClick={end}>{t('End access')}</button>
        </div>
      )}
      {w.status === 'ENDED' && w.minutesUsed > 0 && !w.reviewed && <Review w={w} />}
      <Concern w={w} onSent={onChange} />
    </article>
  );
}

export default function ExpertsPage() {
  const t = useT();
  const access = useAssistAccess();
  const [experts, setExperts] = useState<Expert[]>([]);
  const [work, setWork] = useState<Work[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const confirmed = useRef(false);

  const load = useCallback(() => {
    void api<Expert[]>('/assist/experts', { token: true }).then(setExperts).catch(() => setExperts([]));
    void api<Work[]>('/assist/engagements', { token: true })
      .then((ws) => setWork(ws.map((w) => (w.expertName ? w : { ...w, expertName: translate('A consultant who has left') }))))
      .catch(() => setWork([]));
  }, []);

  useEffect(() => {
    if (!access?.visible) return;
    load();
    if (confirmed.current) return;
    const url = new URL(window.location.href);
    const session = url.searchParams.get('assist_session');
    if (!session) return;
    confirmed.current = true;
    url.searchParams.delete('assist_session');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    if (session === 'cancelled') { setMessage(t('Nothing was paid.')); return; }
    void api(`/assist/confirm?session=${encodeURIComponent(session)}`, { method: 'POST', token: true })
      .then(() => { setMessage(t('Thank you. Your consultant can start now.')); load(); })
      .catch(() => setMessage(t('Thank you. Your payment will show here in a minute.')));
  }, [access?.visible, load]);

  if (!access) return null;
  if (!access.visible) return <div className="card"><h1>{t('Ask a consultant')}</h1><p className="muted">{t('This is not available on this account yet.')}</p></div>;

  return (
    <div className="assist-home">
      <div className="card">
        <p className="field-hint" style={{ marginTop: 0 }}><Link to="/assist">{t('Assist')}</Link></p>
        <h1 style={{ marginTop: 0 }}>{t('Ask a consultant')}</h1>
        <p style={{ marginBottom: 0 }}>{t('Every consultant here has been checked by EasyLearning: their qualifications, professional registration and an enhanced DBS check. You choose who sees your learner, you see the cost before you pay, and you can end their access at any time.')}</p>
        <p className="field-hint">{t('Consultants are independent professionals, not EasyLearning staff. Each one sets their own rate and is responsible for their own advice.')}</p>
        {!access.released && <p className="field-hint">{t('Only you can see this until it is switched on for everyone.')}</p>}
        {message && <p role="status" className="ai-shop-message">{message}</p>}
      </div>

      {work.length > 0 && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>{t('Your consultants')}</h2>
          <div className="list">{work.map((w) => <WorkCard key={w.id} w={w} onChange={load} />)}</div>
        </div>
      )}

      <div className="card">
        <h2 style={{ marginTop: 0 }}>{t('Consultants')}</h2>
        {experts.length === 0 && <p className="muted">{t('No consultants are available yet.')}</p>}
        <div className="list">
          {experts.map((x) => (
            <article key={x.id} className="assist-expert">
              <div className="studio-type-head">
                <div>
                  <h3 style={{ margin: 0 }}>{x.name}</h3>
                  <p className="muted" style={{ margin: 0 }}>{x.headline}</p>
                </div>
                <p style={{ margin: 0 }}><strong>{money(x.hourPence)}</strong> {t('an hour')}</p>
              </div>
              {x.rating?.average != null && <p style={{ margin: 0 }}>{t('★ {average} from {count} reviews', { average: x.rating.average, count: x.rating.count })}</p>}
              {x.specialisms.length > 0 && <p className="assist-tags">{x.specialisms.map((s) => <span key={s} className="pill">{s}</span>)}</p>}
              <p className="ai-text">{x.bio}</p>
              {(x.rating?.recent ?? []).filter((r) => r.comment).length > 0 && (
                <ul className="assist-log">
                  {x.rating.recent.filter((r) => r.comment).slice(0, 3).map((r) => (
                    <li key={r.id}>{t('{rating} out of 5', { rating: r.rating })} · {r.comment}</li>
                  ))}
                </ul>
              )}
              <details>
                <summary className="studio-data-summary">{t('Qualifications and checks')}</summary>
                <p className="ai-text">{x.qualifications}</p>
                {x.registration && <p>{t('Registration: {registration}', { registration: x.registration })}</p>}
                <p>{x.dbsChecked && x.dbsDate
                  ? t('DBS checked by EasyLearning on {date}', { date: new Date(x.dbsDate).toLocaleDateString() })
                  : t('No DBS check recorded.')}</p>
              </details>
              {open === x.id
                ? <Booking expert={x} onDone={() => setOpen(null)} onSent={() => { setOpen(null); setMessage(t('Request sent. {name} will reply with an estimate.', { name: x.name })); load(); }} />
                : <div className="form-actions"><button type="button" className="btn" onClick={() => setOpen(x.id)}>{t('Ask {name}', { name: x.name })}</button></div>}
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
